import { useRef, useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import Router from 'next/router'
import { getDetails, getDetailsKey } from '../../../api-client/account'
import { Desktop, Mobile } from '../../../components/media'
import PageTitle from '../../../components/page-title'
import Accordion from '../../../components/accordion'
import { Button } from '../../../components/button'
import { ROUTES } from '../../../constants'
import UserCard from '../../account/user-card'
import VerificationStatus from './verification-status'
import YourDetails from './your-details'
import Consent from './consent'
import { useAuth } from '../../../contexts/auth'

const ACCORDIONS = {
  VerificationConsent: 'verificationConsent',
  YourDetails: 'yourDetails',
  ConfirmIdentity: 'confirmIdentity',
}

const initialAccordionsState = {
  verificationConsent: {
    title: 'Verification Consent',
    name: ACCORDIONS.VerificationConsent,
    open: true,
    completed: false,
  },
  yourDetails: {
    title: 'Your Details',
    name: ACCORDIONS.YourDetails,
    open: false,
    completed: false,
  },
  confirmIdentity: {
    title: 'Confirm Identity',
    name: ACCORDIONS.ConfirmIdentity,
    open: false,
    completed: false,
  },
}

const initialAccountDetails = {
  driverLicenseNumber: '',
  driverLicenseStateId: '',
  driverLicenseExpirationDate: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  houseNumber: '',
  unitNumber: '',
  street: '',
  streetType: '',
  city: '',
  stateId: '',
  mobileNumber: '',
}

export default function GetVerified() {
  const accordionsRef = useRef({})
  const [verificationState, setVerificationState] = useState({
    consent: false,
    isVerificationRequestAllowed: true,
  })
  const [accordionsState, setAccordionsState] = useState(initialAccordionsState)
  const [accountDetails, setAccountDetails] = useState(initialAccountDetails)
  const { data } = useQuery({ queryKey: getDetailsKey, queryFn: getDetails })

  const [{ userDetails }, { authResync }] = useAuth()

  const handleAccordionToggle = useCallback(
    (name, open) =>
      setAccordionsState((prev) => ({
        ...prev,
        [name]: { ...prev[name], open },
      })),
    [],
  )

  const openAsSolitaryAccordion = useCallback((name) => {
    setAccordionsState((prev) =>
      Object.values(prev).reduce(
        (state, accordion) => ({
          ...state,
          [accordion.name]: { ...accordion, open: name === accordion.name },
        }),
        {},
      ),
    )
  }, [])

  const markAccordionAsCompleted = useCallback(
    (name, completed) =>
      setAccordionsState((prev) => ({ ...prev, [name]: { ...prev[name], completed } })),
    [],
  )

  const handleYourDetailsAccordionToggle = useCallback(
    (name, open) => {
      if (accordionsState.yourDetails.completed && open) openAsSolitaryAccordion(name)
      else handleAccordionToggle(name, open)
    },
    [handleAccordionToggle, openAsSolitaryAccordion, accordionsState.yourDetails.completed],
  )

  const handleConfirmIdentityAccordionToggle = useCallback(
    (name, open) => {
      if (open) setVerificationState((prev) => ({ ...prev, isVerificationRequestAllowed: false }))

      handleAccordionToggle(name, open)
    },
    [handleAccordionToggle],
  )

  const handleConsentRevoke = useCallback(() => {
    setVerificationState({ consent: false, isVerificationRequestAllowed: false })

    setAccordionsState(initialAccordionsState)
  }, [])

  const handleConsentNextClick = useCallback(() => {
    setVerificationState((prev) => ({ ...prev, consent: true }))

    openAsSolitaryAccordion(ACCORDIONS.YourDetails)

    markAccordionAsCompleted(ACCORDIONS.VerificationConsent, true)
    accordionsRef.current.yourDetails.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [openAsSolitaryAccordion, markAccordionAsCompleted])

  const handleAccountDetailsNextClick = useCallback(() => {
    markAccordionAsCompleted(ACCORDIONS.YourDetails, true)

    setVerificationState((prev) => ({ ...prev, isVerificationRequestAllowed: true }))

    openAsSolitaryAccordion(ACCORDIONS.ConfirmIdentity)
    accordionsRef.current.confirmIdentity.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [openAsSolitaryAccordion, markAccordionAsCompleted])

  const handleVerificationFinish = useCallback(() => {
    Router.push(ROUTES.Account).then(() => authResync())
  }, [authResync])

  const handleUserCardCTAButtonClick = useCallback(() => Router.push(ROUTES.Home), [])

  return (
    <>
      <Mobile>
        <section className='pageDetails flex alignCenter flexDirectionColumn'>
          <PageTitle>Get Verified </PageTitle>
        </section>
      </Mobile>

      <div className='bgWrapper'>
        <div className='container flex alignStart'>
          <Desktop>
            <UserCard
              CTAButtonText='Exit'
              onCTAButtonClick={handleUserCardCTAButtonClick}
              handleAccountDetailsChange={setAccountDetails}
            />
          </Desktop>

          <div className='accordionsContainer'>
            <Accordion
              ref={(ref) => (accordionsRef.current['verificationConsent'] = ref)}
              title={accordionsState.verificationConsent.title}
              open={accordionsState.verificationConsent.open}
              name={accordionsState.verificationConsent.name}
              onToggle={handleAccordionToggle}
            >
              <Consent onNext={handleConsentNextClick} onConsentRevoke={handleConsentRevoke} />
            </Accordion>

            <Accordion
              ref={(ref) => (accordionsRef.current['yourDetails'] = ref)}
              title={accordionsState.yourDetails.title}
              open={verificationState.consent && accordionsState.yourDetails.open}
              name={accordionsState.yourDetails.name}
              onToggle={handleYourDetailsAccordionToggle}
            >
              <YourDetails
                prefillData={data}
                accountDetails={accountDetails}
                setAccountDetails={setAccountDetails}
                onNext={handleAccountDetailsNextClick}
              />
            </Accordion>

            <Accordion
              ref={(ref) => (accordionsRef.current['confirmIdentity'] = ref)}
              title={accordionsState.confirmIdentity.title}
              open={
                verificationState.consent &&
                accordionsState.yourDetails.completed &&
                accordionsState.confirmIdentity.open
              }
              name={accordionsState.confirmIdentity.name}
              onToggle={handleConfirmIdentityAccordionToggle}
            >
              <VerificationStatus
                onFinish={handleVerificationFinish}
                shouldPostVerificationRequest={verificationState.isVerificationRequestAllowed}
              />
            </Accordion>
          </div>
        </div>

        <Mobile>
          <div className='floatingBtnsContainer flex justifyBetween'>
            <Button color='alert' className='exit' onClick={Router.back}>
              Exit
            </Button>
          </div>
        </Mobile>
      </div>

      <style jsx>{`
        .bgWrapper {
          background: var(--secondary-bg-color);
        }

        .viewProfile {
          display: block;
          margin: 0.7rem auto 1.7rem;
          color: var(--primary-color);
          font-size: 1.4rem;
        }

        .container {
          max-width: 193rem;
          width: 100%;
          margin: 0 auto;
          padding: 3.3rem 4rem 3rem;

          .accordionsContainer {
            flex: 1;
            max-width: calc(100% - 45.6rem);
            margin: 0 0 0 3.6rem;
            padding: 3.3rem 0;
          }

          :global(.accordion) {
            margin: 0 0 1.3rem;
          }
        }

        .floatingBtnsContainer {
          position: fixed;
          left: 1.5rem;
          bottom: 2.1rem;
          width: calc(100vw - 3rem);

          :global(button) {
            width: calc(100% - 0.35rem);
          }
        }

        :global(.floatingExitBtn) {
          position: fixed;
          left: 1.5rem;
          bottom: 2.1rem;
          width: calc(100vw - 3rem);
        }

        @media (max-width: 1024px) {
          .pageDetails {
            margin: 0 0 1.8rem;

            :global(.pageTitle) {
              margin: 1.9rem 0 0.8rem;
              font-size: 1.8rem;
            }
          }

          .container {
            display: block;
            max-width: 100%;
            min-height: calc(100vh - 12.3rem);
            padding: 2rem 1.1rem;

            :global(.userCard) {
              position: relative;
              top: unset;
              margin: 0 auto 1.4rem;
              padding: 0 0.5rem;
            }

            .accordionsContainer {
              margin: 1.1rem auto;
              padding: 0;
              max-width: 100%;
            }
          }
        }
      `}</style>
    </>
  )
}
